export async function makeDelay(sec?: number): Promise<void> {
	await new Promise(resolve => {
		let count = 0;
		const interval = setInterval(() => {
			count++;
			if (count >= (sec ?? 3)) {
				clearInterval(interval);
				resolve(true);
			}
		}, 1000);
	});
}

export const preventScroll = {
	userSelect: 'none',
	overflowX: 'hidden',
	overflowY: 'hidden',
	touchAction: 'none',
	WebkitOverflowScrolling: 'auto',
	overscrollBehavior: 'none',
	overscrollBehaviorY: 'none',
	overscrollBehaviorX: 'none',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
	'&, & *': {
		overscrollBehavior: 'none',
		overscrollBehaviorY: 'none',
		overscrollBehaviorX: 'none',
	},
};

export const allowScrollY = {
	overflowX: 'hidden',
	overflowY: 'auto',
	touchAction: 'pan-y',
	WebkitOverflowScrolling: 'none',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
};

export const allowScrollX = {
	overflowX: 'auto',
	overflowY: 'hidden',
	touchAction: 'pan-x',
	WebkitOverflowScrolling: 'touch',
	overscrollBehavior: 'contain',
	scrollbarWidth: 'none',
	msOverflowStyle: 'none',
	'&::-webkit-scrollbar': {display: 'none'},
};

export function isMobileDevice(): boolean {
	const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
	return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
}

export function isLocalHost(): boolean {
	if (typeof window === 'undefined') return false;
	const {hostname} = window.location;
	return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function isStandaloneApp(): boolean {
	return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
}

export function getViewportHeight(isStandalone: boolean): string {
	if (isStandalone) return 'calc(var(--inset-area-vvh, var(--inset-area-vh, 1vh)) * 100)';
	if (isMobileDevice()) return 'calc(var(--inset-area-vh, 1vh) * 100)';
	return '100vh';
}

export function formatDateTime(value: any): string {
	const date = new Date(Number(value));
	if (Number.isNaN(date.getTime())) return '';
	const pad = (n: number) => String(n).padStart(2, '0');
	const res = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
	return res;
}

export function stripSSMLTags(ssml: string): string {
	return ssml
		.replace(/<[^>]+>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
}

export function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

export function parseColor(color: string) {
	const trimmed = color.trim();
	if (trimmed.startsWith('#')) {
		const hex = trimmed.slice(1);
		if (hex.length === 3 || hex.length === 4) {
			const r = parseInt(hex[0] + hex[0], 16);
			const g = parseInt(hex[1] + hex[1], 16);
			const b = parseInt(hex[2] + hex[2], 16);
			const a = hex.length === 4 ? parseInt(hex[3] + hex[3], 16) / 255 : 1;
			return {r, g, b, a};
		}
		if (hex.length === 6 || hex.length === 8) {
			const r = parseInt(hex.slice(0, 2), 16);
			const g = parseInt(hex.slice(2, 4), 16);
			const b = parseInt(hex.slice(4, 6), 16);
			const a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
			return {r, g, b, a};
		}
	}

	if (trimmed.startsWith('rgb')) {
		const parts = trimmed.match(/[\d.]+/g);
		if (parts) {
			const [r, g, b, a] = parts.map(Number);
			return {
				r: clamp(Math.round(r), 0, 255),
				g: clamp(Math.round(g), 0, 255),
				b: clamp(Math.round(b), 0, 255),
				a: parts.length === 4 ? clamp(a, 0, 1) : 1,
			};
		}
	}

	return {r: 255, g: 255, b: 255, a: 1};
}

export function withAlpha(color: string, alpha: number) {
	const {r, g, b} = parseColor(color);
	return `rgba(${r}, ${g}, ${b}, ${clamp(alpha, 0, 1)})`;
}
